import * as React from "react"
import { BellOffIcon, ChevronRightIcon, Loader2Icon } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"

import { cn, UserAvatar } from "@ibada/ui"
import { NOTIFICATION_INBOX_WINDOW_DAYS } from "@ibada/shared-types"

import { useTranslations } from "@/i18n"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import { formatShortRelativeTime } from "@/shared/lib/dates/date-fns-locale"
import { buildCanonicalBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useNotificationLanguage } from "@/features/notifications/hooks/use-notification-language"
import { groupNotificationsByTime, NotificationTimeBucket } from "@/features/notifications/lib/group-notifications-by-time"
import { flattenNotificationPages } from "@/features/notifications/lib/notification-inbox-pages"
import { openNotificationAction } from "@/features/notifications/lib/open-notification-action"
import { type NotificationDto, NotificationStatus } from "@/features/notifications/model/notifications.types"
import {
  useInfiniteNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsUnreadCountQuery,
} from "@/features/notifications/services/notifications-query"

const PAGE_SIZE = 15

/** One notification as a row: who did it, what happened (two lines at most), when, and a dot while unread. */
function NotificationRow({ notification, onSelect }: { notification: NotificationDto; onSelect: (notification: NotificationDto) => void }) {
  const t = useTranslations("notifications")
  const language = useNotificationLanguage()
  const unread = notification.status === NotificationStatus.Unread
  const critical = notification.priority === "CRITICAL"
  const title = notification.title?.trim() || t("fallback.title")
  const message = notification.message?.trim() ?? ""
  const actorName = notification.actor?.name ?? t("systemActor")
  return (
    <button type='button' onClick={() => onSelect(notification)} className='flex w-full items-start gap-3 px-4 py-3 text-left active:bg-muted'>
      <UserAvatar name={actorName} src={notification.actor?.avatarUrl ?? undefined} className='mt-0.5 size-10 shrink-0' />
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='flex min-w-0 items-baseline gap-2'>
          <span className={cn("min-w-0 flex-1 truncate text-[15px] leading-snug", unread ? "font-extrabold text-foreground" : "font-semibold text-foreground/80")}>
            {title}
          </span>
          <span className='shrink-0 text-xs font-semibold text-muted-foreground'>{formatShortRelativeTime(notification.createdAt, language)}</span>
        </span>
        {message ? <span className='line-clamp-2 text-[13px] font-semibold break-words text-muted-foreground'>{message}</span> : null}
      </span>
      <span
        aria-hidden
        className={cn("mt-2 size-2.5 shrink-0 rounded-full", unread ? (critical ? "bg-destructive" : "bg-primary") : "bg-transparent")}
      />
    </button>
  )
}

/**
 * The notifications on a phone: a full page rather than a side panel. "Tout lire" in the top bar while anything is
 * unread, the notifications grouped by day as rows, more loading as the list scrolls, and the full notifications
 * page linked at the end. Same queries and read state as the desktop panel.
 */
export function MobileNotificationsScreen({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("notifications")
  const navigate = useNavigate()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const inboxQuery = useInfiniteNotificationsQuery({ limit: PAGE_SIZE })
  const unreadCountQuery = useNotificationsUnreadCountQuery()
  const markReadMutation = useMarkNotificationReadMutation()
  const markAllMutation = useMarkAllNotificationsReadMutation()
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = inboxQuery
  const items = React.useMemo(() => flattenNotificationPages(inboxQuery.data?.pages), [inboxQuery.data?.pages])
  const groups = React.useMemo(() => groupNotificationsByTime(items), [items])
  const hasUnread = (unreadCountQuery.data?.unreadCount ?? 0) > 0
  const loading = inboxQuery.isLoading || (inboxQuery.isFetching && items.length === 0)
  const sectionLabel: Record<NotificationTimeBucket, string> = {
    [NotificationTimeBucket.Today]: t("sections.today"),
    [NotificationTimeBucket.Yesterday]: t("sections.yesterday"),
    [NotificationTimeBucket.ThisWeek]: t("sections.thisWeek"),
    [NotificationTimeBucket.Earlier]: t("sections.earlier"),
  }

  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const sentinel = loadMoreRef.current
    if (!open || !sentinel || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: "160px" },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length, open])

  const select = async (notification: NotificationDto) => {
    if (notification.status === NotificationStatus.Unread) {
      await markReadMutation.mutateAsync({ id: notification.id, status: notification.status }).catch(() => undefined)
    }
    onOpenChange(false)
    openNotificationAction(notification, navigate)
  }

  return (
    <MobileDetailScreen
      open={open}
      onOpenChange={onOpenChange}
      title={t("dropdown.title")}
      backLabel={t("mobile.close")}
      action={
        hasUnread
          ? { label: t("mobile.readAll"), onPress: () => void markAllMutation.mutateAsync(), disabled: markAllMutation.isPending }
          : undefined
      }
    >
      {loading ? (
        <div className='flex flex-col gap-3'>
          <div className='h-24 animate-pulse rounded-2xl bg-card' />
          <div className='h-40 animate-pulse rounded-2xl bg-card' />
        </div>
      ) : inboxQuery.isError ? (
        <div className='flex flex-col items-center gap-3 px-6 pt-24 text-center'>
          <p className='text-base font-extrabold'>{t("error.title")}</p>
          <p className='text-sm font-semibold text-muted-foreground'>{t("error.description")}</p>
          <button type='button' onClick={() => void inboxQuery.refetch()} className='mt-2 h-11 rounded-full bg-primary px-5 text-sm font-extrabold text-primary-foreground'>
            {t("actions.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className='flex flex-col items-center gap-2 px-8 pt-28 text-center'>
          <span className='flex size-16 items-center justify-center rounded-full bg-card text-muted-foreground'>
            <BellOffIcon className='size-7' aria-hidden />
          </span>
          <p className='mt-2 text-lg font-extrabold'>{t("empty.title")}</p>
          <p className='text-sm font-semibold text-muted-foreground'>{t("empty.description")}</p>
        </div>
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.bucket} className='flex flex-col'>
              <h2 className='px-4 pb-1.5 text-sm font-bold text-muted-foreground'>{sectionLabel[group.bucket]}</h2>
              <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>
                {group.items.map((notification) => (
                  <NotificationRow key={notification.id} notification={notification} onSelect={(next) => void select(next)} />
                ))}
              </div>
            </section>
          ))}
          {hasNextPage ? (
            <div ref={loadMoreRef} className='flex justify-center py-3 text-muted-foreground'>
              {isFetchingNextPage ? <Loader2Icon className='size-4 animate-spin' aria-hidden /> : null}
            </div>
          ) : (
            <p className='px-4 text-center text-xs font-semibold text-muted-foreground'>
              {t("endOfList.all", { days: NOTIFICATION_INBOX_WINDOW_DAYS })}
            </p>
          )}
          <Link
            to={buildCanonicalBranchAdminUrl(workspaceSlug, branchSlug, "/notifications")}
            onClick={() => onOpenChange(false)}
            className='flex min-h-[3.25rem] items-center gap-3 rounded-2xl bg-card px-4 active:bg-muted'
          >
            <span className='min-w-0 flex-1 truncate text-base font-semibold'>{t("dropdown.viewAll")}</span>
            <ChevronRightIcon aria-hidden className='size-4 shrink-0 text-muted-foreground/50' />
          </Link>
        </>
      )}
    </MobileDetailScreen>
  )
}
